import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, Sparkles, Check, TrendingUp } from 'lucide-react';
import { useAddToChat } from '../../../components/chat/chatContext';

const GameCardWithPrediction = ({
  game,
  onShowOdds,
  isSelected,
  isFrozen = false,
  onToggleSelection,
  prediction,
  showPredictionButton = true
}) => {
  const navigate = useNavigate();
  const handleAddToChat = useAddToChat();

  // Handler to stage prediction and navigate to chat
  const handleAnalyzeInChat = async () => {
    if (!prediction) return;

    const message = `Analyze this baseball prediction:

${game.teams?.away?.name} @ ${game.teams?.home?.name}

Prediction: ${prediction.winner}${prediction.confidenceNumeric ? ` (${prediction.confidenceNumeric}% confidence)` : ''}
Confidence Level: ${prediction.confidence}
${prediction.reason ? `Reasoning: ${prediction.reason}` : ''}

Can you provide a deeper analysis of this matchup?`;

    await handleAddToChat(message);
    navigate('/chat');
  };

  // Format time to ET format (12-hour with AM/PM)
  const formatTimeToET = (timeString, dateString) => {
    if (!timeString) return 'TBD';

    // TIMEZONE FIX: If we have both date and time, properly convert to ET
    // Backend may send times in UTC-6 (CST) or other timezones
    if (dateString && timeString) {
      try {
        // Combine date and time to create a full datetime string
        // Backend times without timezone info are treated as UTC
        const dateTimeString = `${dateString}T${timeString}`;
        const date = new Date(dateTimeString);

        // Check if date is valid
        if (!isNaN(date.getTime())) {
          // Convert to ET timezone (America/New_York handles DST automatically)
          return date.toLocaleString('en-US', {
            timeZone: 'America/New_York',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          }).replace(/\s+/g, ' ') + ' ET';
        }
      } catch (e) {
        // Fall through to simple formatting if parsing fails
      }
    }

    // Fallback: If time already includes AM/PM, assume it's already in ET
    if (timeString.includes('AM') || timeString.includes('PM')) {
      return timeString.includes('ET') ? timeString : `${timeString} ET`;
    }

    // Fallback: Convert 24-hour format to 12-hour format (no timezone conversion)
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;

    return `${hour12}:${minutes} ${ampm} ET`;
  };

  const statusShort = game.status?.short;
  const isLive = statusShort?.startsWith('IN') || statusShort === 'LIVE' || statusShort === 'IN_PLAY';
  const isCompleted = statusShort === 'FT' || statusShort === 'AOT';
  const isUpcoming = statusShort === 'NS';
  const canPredict = isUpcoming || isLive;
  
  // Get display text for status badge
  const getStatusDisplay = () => {
    if (isLive) {
      // For baseball, show inning if available
      if (game.status?.long?.includes('Inning')) {
        const inning = game.status.long.match(/(\d+)\w*\s+Inning/)?.[1];
        return inning ? `LIVE ${inning}` : 'LIVE';
      }
      return 'LIVE';
    }
    if (isCompleted) return 'FT';
    if (statusShort === 'POST') return 'PPD';
    return null;
  };
  
  const statusDisplay = getStatusDisplay();

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.96, backgroundColor: "rgba(6, 182, 212, 0.1)" }}
      className={`relative bg-white/[0.03] rounded-xl p-4 hover:bg-white/[0.05] transition-all duration-200 border ${
        isSelected 
          ? 'border-emerald-500/50 bg-emerald-500/10' 
          : 'border-white/[0.05] hover:border-white/10'
      } active:bg-white/[0.07]`}
    >
      {/* Status Badge - Top Right */}
      {statusDisplay && (
        <div className="absolute top-3 right-3 z-10">
          <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full ${
            isLive ? 'bg-red-500' : statusShort === 'POST' ? 'bg-yellow-500/20' : 'bg-white/20'
          }`}>
            {isLive && <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />}
            <span className="text-[10px] font-bold text-white">
              {statusDisplay}
            </span>
          </div>
        </div>
      )}
      
      {/* League Label */}
      <div className="text-xs text-white/65 font-semibold uppercase tracking-wider mb-3 truncate pr-16">
        {game.league?.name || 'MLB'}
      </div>

      {/* Teams and Scores */}
      <div className="space-y-3">
        {/* Away Team */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {game.teams?.away?.logo && (
              <img 
                src={game.teams.away.logo} 
                alt={game.teams.away.name}
                className="w-10 h-10 object-contain flex-shrink-0"
                loading="lazy"
              />
            )}
            <span className="text-sm font-semibold text-white truncate">
              {game.teams?.away?.name || 'Away Team'}
            </span>
          </div>
          <span className="text-xl font-black text-white ml-3 tabular-nums">
            {isLive || isCompleted ? (game.scores?.away?.total ?? 0) : '-'}
          </span>
        </div>

        {/* Home Team */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {game.teams?.home?.logo && (
              <img 
                src={game.teams.home.logo} 
                alt={game.teams.home.name}
                className="w-10 h-10 object-contain flex-shrink-0"
                loading="lazy"
              />
            )}
            <span className="text-sm font-semibold text-white truncate">
              {game.teams?.home?.name || 'Home Team'}
            </span>
          </div>
          <span className="text-xl font-black text-white ml-3 tabular-nums">
            {isLive || isCompleted ? (game.scores?.home?.total ?? 0) : '-'}
          </span>
        </div>
      </div>

      {/* Prediction Display */}
      {prediction && (
        <div className="mt-3 pt-3 border-t border-white/10 bg-violet-500/10 rounded-lg p-3">
          <div className="flex items-center gap-2 text-sm">
            <Sparkles size={16} className="text-violet-300" />
            <span className="text-white/80 font-medium">AI Prediction:</span>
            {typeof prediction === 'object' && prediction.winner ? (
              <>
                <span className="font-bold text-white">{prediction.winner}</span>
                {prediction.confidenceNumeric && (
                  <span className="text-white/90 font-medium">({prediction.confidenceNumeric}%)</span>
                )}
                <span className={`ml-2 px-2 py-0.5 rounded text-xs font-bold ${
                  prediction.confidence === 'HIGH' ? 'bg-green-500/20 text-green-300' :
                  prediction.confidence === 'MED' ? 'bg-yellow-500/20 text-yellow-300' :
                  'bg-red-500/20 text-red-300'
                }`}>
                  {prediction.confidence}
                </span>
              </>
            ) : (
              <span className="font-medium text-white">{prediction.full || prediction}</span>
            )}
          </div>
          {prediction.reason && (
            <p className="text-xs text-white/60 mt-1 line-clamp-2">
              {prediction.reason}
            </p>
          )}
          <button
            onClick={handleAnalyzeInChat}
            className="text-xs text-violet-400 hover:text-violet-300 hover:underline mt-2 inline-block transition-colors cursor-pointer bg-transparent border-none p-0"
          >
            Analyze in Chat →
          </button>
          {/* Show cached indicator if prediction is cached */}
          {prediction.cached && (
            <div className="text-xs text-yellow-400 mt-1 font-semibold">
              Cached • {prediction.cooldownRemaining}m remaining
            </div>
          )}
        </div>
      )}

      {/* Game Time - only for upcoming games */}
      {!isCompleted && !isLive && (
        <div className="mt-3 pt-3 border-t border-white/10">
          <div className="flex items-center justify-center gap-1 text-white/50">
            <Clock size={12} />
            <span className="text-xs font-medium">
              {formatTimeToET(game.time, game.date)}
            </span>
          </div>
        </div>
      )}
      
      {/* Action Buttons */}
      <div className="flex gap-2 mt-3">
        {/* Prediction Button */}
        {showPredictionButton && canPredict && onToggleSelection && (
          <button
            onClick={() => {
              if (!isFrozen) {
                onToggleSelection(game);
              }
            }}
            disabled={isFrozen}
            className={`flex-1 flex items-center justify-center gap-1 text-xs px-3 py-2 rounded-lg transition-all font-bold ${
              isFrozen
                ? 'bg-yellow-500/20 text-yellow-300 opacity-60 cursor-not-allowed'
                : isSelected 
                ? 'bg-emerald-500 text-white shadow-lg' 
                : isLive 
                  ? 'bg-red-500/20 text-red-300 hover:bg-red-500/30'
                  : 'bg-violet-500/20 text-violet-300 hover:bg-violet-500/30'
            }`}
          >
            {isFrozen ? (
              <>
                <Clock size={14} />
                Cooldown
              </>
            ) : isSelected ? (
              <>
                <Check size={14} />
                Selected
              </>
            ) : (
              <>
                <Sparkles size={14} />
                {isLive ? 'Live Forecast' : 'Predict'}
              </>
            )}
          </button>
        )}
        
        {/* Odds Button */}
        {onShowOdds && (
          <button
            onClick={() => onShowOdds(game)}
            className="flex-1 flex items-center justify-center gap-1 text-xs px-3 py-2 bg-emerald-500/20 text-emerald-300 rounded-lg hover:bg-emerald-500/30 transition-colors font-bold"
          >
            <TrendingUp size={14} />
            View Odds
          </button>
        )}
      </div>
    </motion.div>
  );
};

export default GameCardWithPrediction;